// @ts-strict-ignore
import { WireContourService } from '@3d-scene/common/util/wire-contour-service';
import { MaterialsModel } from '@3d-scene/service/materials.model';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { Dictionary } from 'typescript-collections';
import { AxesHelper, Box3, DoubleSide, Group, Material, Mesh, Object3D, Quaternion, Vector3 } from 'three';
import { ProjectModel } from '@project/model/project.model';
import { DynamicConnectionCreator } from './dynamic-connection-creator';
import { CreateLabelingHelper } from '@project/components/label/helper/create-labeling.helper';
import { ArticleLabelingManager } from '@project/components/article/manager/article-labeling-manager';
import { LabelingArticleRotationHelper } from '@project/components/label/helper/labeling-article-rotation.helper';
import { ArrayUtils } from '@shared/util/array-utils';
import { StringUtils } from '@shared/util/string-utils';
import { BaseContainer3D } from './base-container-3d';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { Connection } from './connection';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ConnectionAreaUsageVO, ConnectionAreaVO, MetaElementUsageIdentityVO, MetaElementUsageVO, MetaElementVO, SelectionMetaElementUsageIdentity, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { LabelingUtils } from './util/labeling-utils';
import { ArticlePostParseProcessor } from '@project/components/article/processor/postparse/article-post-parse.processor';
import { MeshUtils } from '../common/util/mesh-utils';
import { Object3DUtils } from '../common/util/object-3d-utils';
import { TextureConnectionInfo } from './texture-connection-info';
import { SceneManager } from '../manager/scene.manager';
import { ProjectHelper } from '@project/helper/project.helper';
import { ArticleMoveManager } from '@project/components/article/manager/article-move.manager';
import type { CompositionContainer } from './composition-container';
import { ArticleManager } from '@article/manager/article.manager';
import { Log } from '@shared/debug/log';
import { ArticleModelDataHolder, ParsedArticleModelData } from './cache/model-data';
import { Subscription } from 'rxjs';
import { WireBridgeService } from '@project/components/article/manager/wire-bridge.service';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { CreateNewProjectWithPresetArticlesManager } from '@project/manager/create-new-project-with-preset-articles.manager';
import { LayerManager } from '../manager/layer-manager';
import { CompositeArticleManager } from '@article/manager/composite-article.manager';
import { ArticleLoadingManager } from '@article/service/article-loading.manager';
import { ArticlesVisibilityService } from '@project/components/article/service/articles-visibility.service';
import { BridgeArticleManager } from '@project/components/article/manager/bridge-article.manager';
import { CarrierBlockManager } from '@project/components/article/manager/carrier-block.manager';
import { ConnectionFactory } from './connection.factory';
import { ContainerService } from './container.service';
import { ArticleContainerAttachedService, AttachedArticleContainer } from '@project/components/article/service/article-container-attached.service';
import { ProjectChanged, ProjectChangedType } from '@project/model/project-changed';
import { ArticleChangedService } from '../service/article-changed.service';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectDrillingHoleService } from '@project/service/project-drilling-hole.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ConnectionInfoUtils } from './util/connection-info-utils';
import { AttachCodingFingerArticleService } from '@project/components/article/service/attach-coding-finger-article.service';
import { MetaElementUtils } from '@article/components/connections/connection-creation/meta-element.utils';
import { DebugDraw } from '@shared/debug/debug-draw';
import { LabeledFlag } from './labeled-flag';
import { IsThreeJsType } from '../common/util/is-threejs-type';

export class ArticleContainer extends BaseContainer3D {
  public static readonly CONTOUR_COLOR: number = 0x000000;
  public static readonly CONTOUR_DRILLINGHOLE_COLOR: number = 0xffffff;
  public static readonly SELECTION_COLOR: number = 0x03c03c;
  public static readonly INVALID_COLOR: number = 0xff0000;
  public static readonly CONTOUR_THICKNESS: number = 1;

  public static readonly WIRE_LINE_NAME: string = 'wireline';
  public static readonly AXISHELPER_NAME: string = 'axishelper';

  private _articleGUID: string;
  private _article: ArticleVO;
  private _articleUsage: ArticleUsageVO;
  public articleObject3DDataShared: boolean = false;

  public wirecontour: Object3D = new Group();

  private _pickConnectorPositionMode: boolean = false;
  private _pickConnectorRotationMode: boolean = false;
  private _pickSurfaceMode: boolean = false;

  private _model: Group = null;
  private _labelingManager: ArticleLabelingManager;
  private _connections: Dictionary<string, Connection> = new Dictionary<string, Connection>();
  private _properties: Dictionary<string, any> = new Dictionary<string, any>();

  public isParsed: boolean = false;

  private _articleSelected: boolean = false;

  private _eventListenersAdded: boolean = false;

  private projectChangedSubscription: Subscription;
  private _articleChangedSubscription: Subscription;

  constructor(
    labelingUtils: LabelingUtils,
    private containerService: ContainerService,
    public projectModel: ProjectModel,
    public projectModelService: ProjectModelService,
    public projectArticleService: ProjectArticleService,
    public projectMetaElementService: ProjectMetaElementService,
    private projectDrillingHoleService: ProjectDrillingHoleService,
    private articleManager: ArticleManager,
    private projectHelper: ProjectHelper,
    private dynamicConnectionCreator: DynamicConnectionCreator,
    public createLabelingHelper: CreateLabelingHelper,
    private articleMoveManager: ArticleMoveManager,
    private sceneManager: SceneManager,
    private projectContentPresenter: ProjectContentPresenter,
    private createNewProjectWithPresetArticlesManager: CreateNewProjectWithPresetArticlesManager,
    private layerManager: LayerManager,
    private compositeArticleManager: CompositeArticleManager,
    private articleLoadingManager: ArticleLoadingManager,
    private articlesVisibilityService: ArticlesVisibilityService,
    private bridgeArticleManager: BridgeArticleManager,
    private carrierBlockManager: CarrierBlockManager,
    private attachCodingFingerArticleService: AttachCodingFingerArticleService,
    private connectionFactory: ConnectionFactory,
    private labelingArticleRotationHelper: LabelingArticleRotationHelper,
    private articlePostParseProcessor: ArticlePostParseProcessor,
    private articleContainerAttachedService: ArticleContainerAttachedService,
    private articleChangedService: ArticleChangedService,
    private materialsModel: MaterialsModel,
    private wireContourService: WireContourService,
  ) {
    super();
    this._labelingManager = new ArticleLabelingManager(this, labelingUtils, containerService);
    this.addAxesHelper();
  }

  public readonly isArticleContainer = true;

  private isAdminView(): boolean {
    return this.projectModel.isAdminView;
  }

  public get articleGUID(): string {
    return this._articleGUID;
  }

  public set articleGUID(value: string) {
    if (value === this._articleGUID) {
      return;
    }
    this._articleGUID = value;
  }

  /**
   * Return article usage. If current usage is null then usage is retrieved from main model.
   */
  public get articleUsage(): ArticleUsageVO {
    if (!this._articleUsage) {
      this._articleUsage = this.projectModel.getArticleUsage(this.articleGUID);
    }
    return this._articleUsage;
  }

  public updateArticleUsage(usage: ArticleUsageVO): void {
    this._articleUsage = usage;
  }

  /**
   * Return article that contains in the container.
   */
  public get article(): ArticleVO {
    if (!this._article && this.articleUsage) {
      this._article = this.articleManager.getCachedArticle(this.articleUsage.articleId, this.articleUsage.articleVersion);
    }
    return this._article;
  }

  public updateArticle(article: ArticleVO): void {
    this._article = article;
  }

  public get model(): Group {
    return this._model;
  }

  private updateModel(model: Group) {
    this.removeEventListeners();
    super.add(model);
    this._model = model;
    this.addEventListeners();

    this.wireContourService.validateWireContour(this);
    this.invalidateBounds();
  }

  public get articleId(): number {
    if (this.article) {
      return this.article.identity;
    }
    return null;
  }

  public get compositeOrArticleId(): number {
    return ArticleUsageUtils.getCompositeOrArticleId(this.articleUsage);
  }

  public get connections(): Connection[] {
    return this._connections.values();
  }

  public get properties(): Dictionary<string, any> {
    return this._properties;
  }

  public set properties(properties: Dictionary<string, any>) {
    this._properties = properties;
  }

  public get articleSelected(): boolean {
    return this._articleSelected;
  }

  public get pickConnectorPositionMode(): boolean {
    return this._pickConnectorPositionMode;
  }

  public set pickConnectorPositionMode(value: boolean) {
    this._pickConnectorPositionMode = value;
  }

  public get pickConnectorRotationMode(): boolean {
    return this._pickConnectorRotationMode;
  }

  public set pickConnectorRotationMode(value: boolean) {
    this._pickConnectorRotationMode = value;
  }

  public get pickSurfaceMode(): boolean {
    return this._pickSurfaceMode;
  }

  public set pickSurfaceMode(value: boolean) {
    this._pickSurfaceMode = value;
  }

  public async parseModel(articleModelDataHolder: ArticleModelDataHolder): Promise<void> {
    const articleVO: ArticleVO = articleModelDataHolder.article;
    if (this.isParsed || !this.article || articleVO.identity !== this.article.identity || articleVO.version !== this.article.version) {
      return;
    }
    this.updateArticle(articleVO);
    await this.executeParseModel(articleModelDataHolder);
  }

  private async executeParseModel(articleModelDataHolder: ArticleModelDataHolder): Promise<void> {
    const connectionNames: string[] = [];
    for (const con of this.article.metaElements) {
      connectionNames.push(con.name);
    }

    if (articleModelDataHolder.isEmptyArticleModelData || ArticleUsageUtils.isArticleUsageWithout3DModel(this.articleUsage)) {
      this.finalizeEmptyModel();
    } else if (articleModelDataHolder.isParsedArticleModelData) {
      const parsedArticleModelData: ParsedArticleModelData = articleModelDataHolder.modelData as ParsedArticleModelData;
      const model: Object3D = parsedArticleModelData.cloneModelData();
      this.updateModel(model as Group);
      this.name = parsedArticleModelData.name;

      await this.finalizeParsedModel(connectionNames, articleModelDataHolder.article.articleNo);
    }
    this.isParsed = true;

    // Dispatching to the application can trigger more parseModel calls,
    // therefore the dispatching of messages has to be done after isParsed = true
    await this.dispatchFinalizeArticleSuccessMessage(this.article);
  }

  private finalizeEmptyModel(): void {
    this._connections = new Dictionary<string, Connection>();
  }

  private async finalizeParsedModel(connNames: string[], articleNo: string): Promise<void> {
    const connections: Connection[] =
      this.convertMeshesToConnections(connNames, articleNo).filter(connection => !StringUtils.isEmpty(connection.id));
    for (let connection of connections) {
      this._connections.setValue(connection.id, connection);
    }
    this.recreateTextureConnectionInfos();
    const textureAreas: TextureAreaVO[] = ArticleUtils.getEditableTextureElements(this.article);
    const predefinedTextureAreas: Dictionary<string, MetaElementVO> = ArticleUtils.metaElementsToMap(textureAreas);
    for (const connection of this.connections) {
      this.updateSideOfConnectionIfNecessary(connection);

      if (!ArticleUtils.isDynamicArticle(this.article) && predefinedTextureAreas.containsKey(connection.id) && this.projectModel.texturesEnabled) {
        const metaElement: MetaElementVO = this.findMetaElementByName(connection.id);
        const connectionMetaUsage: MetaElementUsageVO = this.findMetaElementUsageByName(connection.id);
        LabelingUtils.createTextureMetaElementUsageIfNotExists(
          connectionMetaUsage,
          this.articleGUID,
          metaElement,
          this.projectArticleService,
          this.projectMetaElementService,
          this._articleUsage
        );
      }
    }
    this.updateConnectionElements(this.article, false);
    if (!this.isAdminView()) {
      this.articlePostParseProcessor.parse(this);
    }
    this.refreshMetaElements(this.article, false);
    this.wireContourService.createWireContour(this);
    if (!this._articleUsage.articleVisible) {
      this.hide();
    }
  }

  private updateSideOfConnectionIfNecessary(connection: Connection) {
    for (const metaElement of this.article.metaElements) {
      if (connection.mesh.name === metaElement.name && metaElement instanceof ConnectionAreaVO) {
        if (metaElement.bothsides) {
          (connection.mesh.material as Material).side = DoubleSide;
        }
      }
    }
  }

  private convertMeshesToConnections(connectionNames: string[], articleNo: string): Connection[] {
    const result: Connection[] = new Array<Connection>();
    const connectionMeshes: Mesh[] = ConnectionInfoUtils.getConnectionMeshes(this, connectionNames);
    for (const mesh of connectionMeshes) {
      const connection: Connection = this.connectionFactory.getNewInstance(this, mesh);
      result.push(connection);
    }
    this.logMissingConnections(connectionNames, result, articleNo);
    return result;
  }

  private logMissingConnections(connectionNames: string[], connections: Connection[], articleNo: string) {
    if (Log.isDevMode && connectionNames.length !== connections.length) {
      const connectionNameSet = new Set<string>(connectionNames);
      const existingConnections = connectionNames.concat().sort();
      const missingConnections = connections.map(connection => connection.connectionName).filter(connectionName => !connectionNameSet.has(connectionName)).sort();
      if (missingConnections.length > 0) {
        Log.error(`Article ${articleNo} is missing connection in 3D model!\r\nExisting names: ${JSON.stringify(existingConnections)}\r\nFound missing connections: ${JSON.stringify(missingConnections)}`);
      }
    }
  }

  protected updateConnectionElements(articleVO: ArticleVO, deleteUnusedConnections: boolean = true): void {
    if (this.model) {
      let connectionSet: string;
      const activeSet: string = ArticleUsageUtils.getActiveConnectionSetId(this.articleUsage);
      for (const connection of this.connections) {
        const metaElement: MetaElementVO = this.findMetaElementByNameAndArticle(connection.mesh.name, articleVO);
        connection.type = ConnectionInfoUtils.getMetaElementType(metaElement);

        if (
          metaElement &&
          metaElement instanceof ConnectionAreaVO &&
          ConnectionAreaVO.SIDE_GIVING === metaElement.connectionSide &&
          deleteUnusedConnections &&
          !this.isAdminView()
        ) {
          // keep all connections sets inside admintool
          connectionSet = MetaElementUtils.getConnectionSetId(metaElement);
          if (activeSet === connectionSet) {
            this.updateConnectionElement(connection, metaElement);
          } else {
            this.disposeConnectionByName(metaElement.name);
          }
        } else if (metaElement) {
          this.updateConnectionElement(connection, metaElement);
        } else {
          if (deleteUnusedConnections && !this.isAdminView()) {
            // keep all connections sets inside admintool
            this.disposeConnectionByName(connection.mesh.name);
          }
        }
      }
    }
  }

  private updateConnectionElement(connection: Connection, metaElement: MetaElementVO): void {
    // method is not the same as in flex version
    connection.texturesEnabled = this.projectModel.texturesEnabled;
  }

  public disposeConnectionByName(metaElementName: string): Connection {
    const connection: Connection = this.removeConnectionByName(metaElementName);
    this.disposeConnection(connection);
    return connection;
  }

  public removeConnectionByName(metaElementName: string): Connection {
    return this._connections.remove(metaElementName);
  }

  private disposeConnection(connection: Connection): void {
    if (!connection) {
      return;
    }
    Object3DUtils.destroy(connection.mesh);
  }

  private findMetaElementByNameAndArticle(name: string, article: ArticleVO): MetaElementVO {
    let foundMetaElement: MetaElementVO = null;
    if (!article) {
      foundMetaElement = this.findMetaElementByName(name);
    } else {
      for (const metaElement of article.metaElements) {
        if (name === metaElement.name) {
          foundMetaElement = metaElement;
          break;
        }
      }
    }
    return foundMetaElement;
  }


  public setWireContourMaterial(defaultContourMaterial: LineMaterial, brighterContourMaterial: LineMaterial = defaultContourMaterial): void {
    if (this.wirecontour) {
      this.wirecontour.traverse(mesh => {
        const useBrighterMaterial = mesh.userData.brighter === true;
        (mesh as Mesh).material = useBrighterMaterial ? brighterContourMaterial : defaultContourMaterial;
      });
    }
  }

  public unsetWireContourMaterial(): void {
    if (!this.wirecontour) {
      return;
    }

    const defaultContourMaterial = ArticleUtils.isDrillingHole(this.article) ?
      this.materialsModel.materialContourDrillingHole
      : this.materialsModel.materialContour;

    this.setWireContourMaterial(defaultContourMaterial, this.materialsModel.materialContourGrey);
  }

  public showWirecontour() {
    if (!this.wirecontour) {
      return;
    }
    if (this.articleUsage.articleVisible || this.projectHelper.showWireFramesForHiddenArticles) {
      this.wirecontour.visible = true;
    }
  }

  public hideWirecontour() {
    if (!this.wirecontour) {
      return;
    }
    if (!this.articleUsage.articleVisible && !this.projectHelper.showWireFramesForHiddenArticles) {
      this.wirecontour.visible = false;
    }
  }

  public showConnection(name: string): void {
    const connection: Connection = this.findConnectionByName(name);
    if (connection) {
      connection.mesh.visible = true;
      (connection.mesh.material as Material).visible = true;
    }
  }

  public hideConnection(name: string): void {
    const connection: Connection = this.findConnectionByName(name);
    if (connection) {
      connection.mesh.visible = false;
      (connection.mesh.material as Material).visible = false;
    }
  }

  public hideModel() {
    if (!this.model || !this.model.visible) {
      return;
    }
    this.model.visible = false;
  }

  public showModel() {
    if (!this.model || this.model.visible) {
      return;
    }
    if (this.articleUsage.articleVisible) {
      this.model.visible = true;
    }
  }

  public hide() {
    this.wireContourService.validateWireContour(this);
    this.hideModel();
    this.hideWirecontour();
  }

  public show() {
    this.wireContourService.validateWireContour(this);
    this.showModel();
    this.showWirecontour();
  }

  public extrude(height, direction) {
    let group;
    const oldWirecontour = this.getObjectByName(WireContourService.WIRECONTOUR_NAME);
    if (oldWirecontour) {
      this.remove(oldWirecontour);
    }
    for (let i = 0; i < this.children.length; i++) {
      const meshesToAdd = [];
      let meshesToRemove = [];

      group = this.children[i];
      const meshes = MeshUtils.findGeometryMeshes(group);
      meshesToRemove = meshes;
      meshes.forEach((meshToAdd) => meshesToAdd.push(MeshUtils.extrude(meshToAdd, height, direction)));

      for (let j = 0; j < meshesToAdd.length; j++) {
        if (meshesToAdd[j]) {
          group.remove(meshesToRemove[j]);
          group.add(meshesToAdd[j]);
        }
      }
    }

    this.wireContourService.createWireContour(this);
  }

  public findConnection(name: string, connectionUid: string = ``): Connection {
    if (!this._connections) {
      return null;
    }
    return this._connections.getValue(name);
  }

  public findConnectionByName(name: string): Connection {
    return this._connections.getValue(name);
  }

  public findFirstConnectionByName(name: string): Connection {
    if (StringUtils.isEmpty(name)) {
      return null;
    }
    for (const connection of this.connections) {
      if (connection.id === name) {
        return connection;
      }
    }
    return null;
  }

  public getCurrentArticleGivingAreaIds(): MetaElementUsageIdentityVO[] {
    const result: MetaElementUsageIdentityVO[] = [];
    for (const connectionArea of ArticleUtils.getConnectionElements(this.article, ConnectionAreaVO.SIDE_GIVING)) {
      const metaElementUsage: MetaElementUsageVO = this.findMetaElementUsageByName(connectionArea.name);
      if (metaElementUsage) {
        result.push(metaElementUsage.identity);
      }
    }
    return result;
  }

  public getCurrentArticleAcceptingAreaIds(): MetaElementUsageIdentityVO[] {
    const result: MetaElementUsageIdentityVO[] = [];
    for (const connectionArea of ArticleUtils.getConnectionElements(this.article, ConnectionAreaVO.SIDE_ACCEPTING)) {
      const metaElementUsage: MetaElementUsageVO = this.findMetaElementUsageByName(connectionArea.name);
      if (metaElementUsage) {
        result.push(metaElementUsage.identity);
      }
    }
    return result;
  }

  public findMetaElementUsageByName(metaElementName: string): MetaElementUsageVO {
    return ArticleUsageUtils.findMetaElementUsageByName(this.articleUsage, metaElementName);
  }

  public findMetaElementByName(name: string): MetaElementVO {
    let foundMetaElement: MetaElementVO = null;
    for (let i = 0; i < this.article.metaElements.length; i++) {
      if (name === this.article.metaElements[i].name) {
        foundMetaElement = this.article.metaElements[i];
        break;
      }
    }
    return foundMetaElement;
  }

  private articleChange_handler(data: { articleUsageId: string, updateTextures: boolean }): void {
    this._pickConnectorPositionMode = false;
    this._pickConnectorRotationMode = false;
    this._pickSurfaceMode = false;
    if (!data || this.articleGUID !== data.articleUsageId) {
      return;
    }
    if (ArticleUtils.isDynamicArticle(this.article)) {
      let isInvertedDirection: boolean = false;
      if (ArticleUtils.isLabelingArticle(this.article)) {
        isInvertedDirection = this.labelingArticleRotationHelper.isInvertedLabelingArticleDirection(this);
      }

      const givingConnections: Connection[] = this.dynamicConnectionCreator.createConnectionsForDynamicGivingConnection(this, isInvertedDirection);
      if (ArticleUtils.isLabelingArticle(this.article)) {
        const meshCreatedSuccessful: boolean = this.createLabelingHelper.createTextMeshForDynamicConnections(
          this,
          givingConnections.length === 0 ? null : givingConnections[0],
          this.projectModel.texturesEnabled,
          isInvertedDirection
        );
        if (meshCreatedSuccessful) {
          this.recreateTextureConnectionInfos();
        }
      }
      this.createTextureMetaElementUsages();
      this.refreshTextureConnections(data.updateTextures);
    }
  }

  private createTextureMetaElementUsages(): void {
    this.updateConnectionElements(this.article);
    const textureMetaElements: TextureAreaVO[] = ArticleUtils.getTextureElements(this.article);
    for (const textureArea of textureMetaElements) {
      const connection: Connection = this.findFirstConnectionByName(textureArea.name);
      if (connection) {
        LabelingUtils.applyTextureMetaElementUsageIfNotExists(connection, this.projectArticleService, this.projectMetaElementService, this._articleUsage);
      }
    }
  }

  public setConnection(connection: Connection): void {
    if (!connection) {
      return;
    }
    this._connections.setValue(connection.id, connection);
  }

  public removeConnection(connectionId: string) {
    this._connections.remove(connectionId);
  }

  /**
   * Method provides id of the giving area on this article which is connected to acceptingAreaId.
   * @param acceptingAreaId
   * @return
   *
   */
  public findGivingArea(acceptingAreaId: MetaElementUsageIdentityVO): MetaElementUsageIdentityVO {
    if (this.articleGUID === acceptingAreaId.articleUsageId) {
      return null;
    }
    return ArticleUsageUtils.connectionAreaUsageByAcceptingAreaId(this.articleUsage, acceptingAreaId);
  }

  public addChildToModel(child: Object3D): Object3D {
    if (!child || !this.model) {
      return null;
    }
    if (MeshUtils.isConnection(child.name) && IsThreeJsType.mesh(child)) {
      this._connections.setValue(child.name, this.connectionFactory.getNewInstance(this, child));
    }
    this.model.add(child);
    return child;
  }

  public addChildToModelAndKeepWorldTransform(child: Object3D) {
    const worldPosition = new Vector3();
    const worldQuaternion = new Quaternion();
    child.getWorldPosition(worldPosition);
    child.getWorldQuaternion(worldQuaternion);

    this.addChildToModel(child);

    this.worldToLocal(worldPosition);
    const parentInverseQuat = new Quaternion().copy(this.quaternion).invert();
    worldQuaternion.premultiply(parentInverseQuat);
    child.position.copy(worldPosition);
    child.quaternion.copy(worldQuaternion);
  }

  public removeChildFromModel(child: Object3D): void {
    if (!child || !this.model) {
      return;
    }
    if (MeshUtils.isConnection(child.name)) {
      this._connections.remove(child.name);
    }
    this.model.remove(child);
  }

  public moveArticle(delta: Vector3): void {
    this.articleMoveManager.moveArticle(this, delta);
  }

  public refreshMetaElements(articleVO: ArticleVO = null, deleteUnusedConnections: boolean = true): void {
    this.updateConnectionElements(articleVO ? articleVO : null, deleteUnusedConnections);
    if (this.model && this.articleUsage) {
      for (const metaElementUsage of this.articleUsage.metaElementUsages) {
        for (const connection of this.connections) {
          if (connection.id === metaElementUsage.identity.metaElementName) {
            connection.metaElementUsage = metaElementUsage;
          }
        }
      }
      for (const connection of this.connections) {
        connection.isActive = ConnectionInfoUtils.isActiveConnection(connection, this.isAdminView());
        connection.refresh();
      }
    }
  }

  public get textConnections(): TextureConnectionInfo[] {
    return this._labelingManager ? this._labelingManager.textConnections : null;
  }

  public get imageConnections(): TextureConnectionInfo[] {
    return this._labelingManager ? this._labelingManager.imageConnections : null;
  }

  public findTextureConnectionInfo(name: string): TextureConnectionInfo {
    if (ArrayUtils.isEmpty(this.textConnections) || StringUtils.isEmpty(name)) {
      return null;
    }
    for (const textConnectionInfo of this.textConnections) {
      if (textConnectionInfo.id === name) {
        return textConnectionInfo;
      }
    }
  }

  public recreateTextureConnectionInfos(): void {
    this._labelingManager.fillDefaultTextConnectionsMap();
  }

  private addEventListeners(): void {
    if (!this.model) {
      return;
    }
    this.projectChangedSubscription = this.projectModel.projectChanged$.subscribe(projectChanged => this.projectChangedEvent(projectChanged));
    this._articleChangedSubscription = this.articleChangedService.articleChanged$.subscribe({
      next: (data) => this.articleChange_handler(data),
      error: (error) => Log.error('Error in article changed subscription: ', error)
    });
    this._eventListenersAdded = true;
  }

  public removeEventListeners(): void {
    if (this._eventListenersAdded) {
      this.projectChangedSubscription?.unsubscribe();
      this.projectChangedSubscription = undefined;
      this._articleChangedSubscription?.unsubscribe();
      this._articleChangedSubscription = undefined;
    }
    this._eventListenersAdded = false;
  }

  private projectChangedEvent(projectChanged: ProjectChanged) {
    switch (projectChanged.type) {
      case ProjectChangedType.TexturesUpdated:
        this.refreshTextureConnections(false);
        break;
    }
  }

  public articleSelectedHandler(): void {
    this._articleSelected = true;
    if (this.wirecontour) {
      this.setWireContourMaterial(this.materialsModel.materialContourSelection);
      this.sceneManager.renderScene();
    }
    this.flagSelectionHandler(true);
  }

  public articleDeselectedHandler(): void {
    this._articleSelected = false;
    if (this.wirecontour) {
      this.unsetWireContourMaterial();
      this.sceneManager.renderScene();
    }
    this.flagSelectionHandler(false);
  }

  private flagSelectionHandler(isSelected: boolean) {
    if(ArticleUtils.isWireBridgeArticle(this.article)) {
      const flags = this.model.children.filter(o => o.name == WireBridgeService.FLAG_OBJECT_NAME);

      for (const flag of flags) {
        (flag as LabeledFlag).onSelectionChanged(isSelected);
      }
      this.sceneManager.renderScene();
    }
  }

  public metaElementSelectedHandler(selectionId: MetaElementUsageIdentityVO): void {
    this.changeMetaElementSelected(selectionId, true);
  }

  public metaElementDeselectedHandler(selectionId: MetaElementUsageIdentityVO): void {
    this.changeMetaElementSelected(selectionId, false);
  }

  private changeMetaElementSelected(selectionId: MetaElementUsageIdentityVO, selected: boolean): void {
    if (selectionId.articleUsageId !== this.articleGUID) {
      return;
    }

    let selectedConnection: Connection;
    if (selectionId instanceof SelectionMetaElementUsageIdentity) {
      selectedConnection = this.findConnection(
        (selectionId as SelectionMetaElementUsageIdentity).metaElementName,
        (selectionId as SelectionMetaElementUsageIdentity).connectionUid
      );
    } else {
      selectedConnection = this.findConnectionByName(selectionId.metaElementName);
    }

    if (selectedConnection) {
      selectedConnection.selected = selected;
      this.sceneManager.renderScene();
    }
  }

  public dispose(): void {
    this.removeEventListeners();
    super.dispose();
    Object3DUtils.disposeAllChildren(this);
  }

  public disposeModel(): void {
    this.removeEventListeners();
    Object3DUtils.disposeAllChildren(this);
    Object3DUtils.destroy(this.wirecontour);
    Object3DUtils.destroy(this.model);
    this.wirecontour = null;
    this._model = null;
  }

  protected updateBounds(): void {
    const connectionNames: string[] = [];
    for (const meta of this.article.metaElements) {
      connectionNames.push(meta.name);
    }
    this._bounds = this.calculateBounds(connectionNames);
  }

  protected calculateBounds(excludedNames: string[] = null): Box3 {
    return Object3DUtils.getAxisAlignedBoundingBoxForObjectContainer(this.model, excludedNames);
  }

  public isExportChildren(): boolean {
    return !ArticleUtils.isZoneArticle(this.article);
  }

  public refreshContainer(deleteUnusedConnections: boolean = true): void {
    this.refreshMetaElements(null, deleteUnusedConnections);
  }

  public refreshTextures(): void {
    for (const connection of this.connections) {
      connection.refreshTexture();
    }
  }

  private async dispatchFinalizeArticleSuccessMessage(article: ArticleVO): Promise<void> {
    // if container is already attached to composition container, then dispatch "articleContainerAttached" success event
    if (this.parent) {
      const compositionGuid: string = (this.parent as CompositionContainer).compositionGuid;
      const connectedTo: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(this.articleUsage);
      const data: AttachedArticleContainer = {
        articleGUID: this.articleGUID,
        compositionId: compositionGuid,
        container: this,
        connectedTo: connectedTo,
      };
      this.compositeArticleManager.articleContainerAttachedHandler(data);
      this.projectContentPresenter.articleContainerAttachedHandler(data);
      await this.articleLoadingManager.checkLoadedArticlesStatus(data);
      this.articlesVisibilityService.refreshArticleTypeList();
      this.bridgeArticleManager.refreshPinsVisibility(data);
      this.carrierBlockManager.processAttachedArticleContainer(data);
      this.attachCodingFingerArticleService.processAttachedCodingFinger(data);
      if (ArticleUtils.isMountingRail(article)) {
        await this.projectDrillingHoleService.loadDrillingholePresetWhenLoadingProject();
        await this.createNewProjectWithPresetArticlesManager.mountingRailAttached();
        this.layerManager.updateLayer('ruler');
      }
      this.articleContainerAttachedService.setArticleContainerAttached(data);
    }
  }

  public refreshTextureConnections(updateTextures: boolean = true, deleteUnusedConnections: boolean = true): void {
    this._labelingManager.refreshTextureConnections(this.projectModel.texturesEnabled, updateTextures, deleteUnusedConnections);
    this.sceneManager.renderScene();
  }

  public removeChildGivingUsages(givingArticleGUIDs: string[]): void {
    for (const connectionAreaUsage of ArticleUsageUtils.connectionAreaUsages(this.articleUsage)) {
      const filteredGivingAreaIds =
        connectionAreaUsage.connection.givingAreaIds.filter(id => givingArticleGUIDs.indexOf(id.articleUsageId) === -1);
      connectionAreaUsage.connection.givingAreaIds = filteredGivingAreaIds;
    }
  }

  protected removeChildGivingUsage(givingArticleGUID: string, givingMetaElementName: string = null): void {
    let articleDetached: boolean = false;
    for(const connectionAreaUsage of ArticleUsageUtils.connectionAreaUsages(this.articleUsage)) {
      const givingAreas: MetaElementUsageIdentityVO[] = connectionAreaUsage.connection.givingAreaIds;
      for (const id of givingAreas.concat()) {
        if (id.articleUsageId === givingArticleGUID && (!givingMetaElementName || givingMetaElementName === id.metaElementName)) {
          givingAreas.splice(givingAreas.indexOf(id), 1);
          articleDetached = true;
        }
      }
    }

    if (articleDetached) {
      this.refreshMetaElements();
      this.sceneManager.renderScene();
    }
  }

  /**
   * Removes all meta element usages.
   */
  public detachAllMetaElementUsages(): void {
    for (const metaUsage of this.getCurrentArticleGivingAreaIds()) {
      this.detachMetaElementUsageByName(metaUsage.metaElementName);
    }
  }

  /**
   * Removes usage of meta element of the article with given name.
   */
  public detachMetaElementUsageByName(name: string): void {
    const connectionAreaUsage: ConnectionAreaUsageVO = ArticleUsageUtils.findConnectionAreaUsageByName(this.articleUsage, name);
    if (connectionAreaUsage &&
      connectionAreaUsage.connection &&
      connectionAreaUsage.connection.acceptingAreaId.articleUsageId !== this.articleGUID) {
      ArrayUtils.removeItem(connectionAreaUsage, this.articleUsage.metaElementUsages);
      const connection: Connection = this.findConnectionByName(name);
      if (connection) {
        connection.metaElementUsage = null;
      }
      const identity: MetaElementUsageIdentityVO = connectionAreaUsage.identity;
      const detachedFrom: MetaElementUsageIdentityVO = connectionAreaUsage.connection.acceptingAreaId;

      // Also remove the backward connection in the container we detached from.
      const otherContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(detachedFrom.articleUsageId);
      if (otherContainer) {
        otherContainer.childMetaElementDetached(identity, detachedFrom);
      }

      this.projectContentPresenter.removeArticleNodesFromParentByArticleUsageId(detachedFrom.articleUsageId, identity.articleUsageId);
      this.refreshMetaElements();
    }
  }

  /**
   * Creates usage of meta element with given name to given accepting connection.
   *
   * @return true if connection was attached, otherwise false
   */
  public attachMetaElementUsageByName(name: string, acceptingConnection: Connection): void {
    if (!name || !acceptingConnection) {
      return;
    }
    const givingConnection: Connection = this.findFirstConnectionByName(name);
    if (!givingConnection) {
      return;
    }
    this.projectMetaElementService.createUsageBetweenConnections(givingConnection, acceptingConnection);
    const data: any = {
      identity: givingConnection.metaElementUsage.identity,
      connectedTo: acceptingConnection.metaElementUsage.identity,
    };
    this.projectContentPresenter.metaElementAttachedHandler(data);

    this.refreshMetaElements();
  }

  /**
   * Add child 3d-component to article container model. Method also adds required event listeners.
   */
  public addChildComponentToModel(object: Object3D): void {
    if (this.articleObject3DDataShared) {
      throw new Error('Modifying a shared article geometry is not allowed!');
    }
    if (!object) {
      return;
    }
    this.addChildToModel(object);
    this.wireContourService.validateWireContour(this);
    this.sceneManager.renderScene();
  }

  private childMetaElementDetached(identity: MetaElementUsageIdentityVO, detachedFrom: MetaElementUsageIdentityVO): void {
    if (this.articleGUID === detachedFrom.articleUsageId) {
      this.removeChildGivingUsage(identity.articleUsageId, identity.metaElementName);
    }
  }

  public toggleArticleVisibility(): void {
    this._articleUsage.articleVisible = !this._articleUsage.articleVisible;
    if (this.articleUsage.articleVisible) {
      this.show();
    } else {
      this.hide();
    }
    this.sceneManager.renderScene();
  }

  public setArticleVisibility(value: boolean): void {
    if (!this.articleUsage || this.articleUsage.articleVisible === value) {
      return;
    }
    this.toggleArticleVisibility();
  }

  public changeConnectionKeyInConnectionsMap(name: string, newName: string): void {
    if (StringUtils.isNotEmpty(name) && StringUtils.isNotEmpty(newName)) {
      const connection: Connection = this._connections.getValue(name);
      if (connection) {
        this._connections.remove(name);
        this._connections.setValue(newName, connection);
      }
    }
  }

  public addAxesHelper(): void {
    if ((DebugDraw.active || this.isAdminView()) && !this.children.some((ch) => ch instanceof AxesHelper)) {
      this.add(new AxesHelper(200));
    }
  }

  public restoreConnection(connectionName: string, show?: boolean): void {
    this._labelingManager.restoreDefaultConnection(this.findConnectionByName(connectionName), show);
  }
}
